import { Permission, ProductForSaleDTO, Res } from 'core/api';
import { BaseComponent, Col, Cols, Column, Flex, Format, Row, TextBoxButton } from 'core/ui';
import { Command } from 'pos/common';
import * as React from 'react';
import Keyboard, { KeyboardLayoutObject, KeyboardReactInterface } from 'react-simple-keyboard';
import * as SalesStore from '../SalesStore';
import SimpleKeyboardLayouts from 'simple-keyboard-layouts';
import { SessionProvider } from 'core/infrastructure';
import { SortMode } from './ProductSelectionState';

type Props = {
	products: ProductForSaleDTO[];
	onProductSelected: (product: ProductForSaleDTO) => Promise<Result>;
	onSearchProducts: (text: string, showOnlyThisPosProducts: boolean, showOnlySaleableProducts: boolean) => void;
	onClearSearch: () => void;
	onSortProductsBy: (sortBy: SortMode) => void;
	sortBy: SortMode;
}

type State = {
	input: string;
	layoutName: string;
	showOnlyThisPosProducts: boolean;
	showOnlySaleableProducts: boolean;
}

function getKeyboardLayoutName() {

	const languageCode = navigator.language.substring(0, 2) || 'es';

	if (typeof Intl.DisplayNames === 'undefined') {
		// En navegadores antiguos no soporta este API que permite pasar del isocode al nombre, que es lo que usa para acceder al
		// layout del teclado. Hago a mano los casos más típicos y dejo un fallback a español

		switch (languageCode) {
			case 'en': return 'english';
			case 'fr': return 'french';
			default: return 'spanish';
		}
	}

	const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
	return ((displayNames.of(languageCode)) ?? 'spanish').toLowerCase();
}

const layouts = new SimpleKeyboardLayouts();
const layoutName = getKeyboardLayoutName();

const keyboardConfig: {
	layout: KeyboardLayoutObject,
	display?: { [button: string]: string }
} = {
	layout: (layouts.get(layoutName) ?? layouts.get('spanish')).layout as KeyboardLayoutObject,
	display: undefined
};

if (layoutName === 'spanish') {
	// Para el layout en español, uso uno nuestro "mejor" que el que viene con el teclado
	keyboardConfig.layout = {
		default: [
			'\\ 1 2 3 4 5 6 7 8 9 0 \' ¡ {bksp}',
			'{tab} q w e r t y u i o p \u0301 +',
			'{lock} a s d f g h j k l ñ \u007b \u007d {enter}',
			'{shift} > z x c v b n m , . - {shift}',
			'@ {space}',
		],
		shift: [
			'| ! " # $ % & / ( ) = ? ¿ {bksp}',
			'{tab} Q W E R T Y U I O P \u0308 *',
			'{lock} A S D F G H J K L Ñ \u005b \u005d {enter}',
			'{shift} < Z X C V B N M ; : _ {shift}',
			'@ {space}',
		],
	};

	keyboardConfig.display = {
		'{shift}': '\u21E7',
		'{lock}': '\u21EB',
		'{tab}': '\u2B7E',
		'{enter}': '\u00A0\u00A0\u00A0\u23CE\u00A0\u00A0\u00A0',
		'{bksp}': '\u00A0\u21E6\u00A0',
	};
}



export class SearchByText extends BaseComponent<Props, State> {

	private keyboardRef: Nullable<KeyboardReactInterface> = null;

	constructor(props: Props) {
		super(props);
	}

	createInitialState(_props: Props): Nullable<State> {
		return {
			input: '',
			layoutName: 'default',
			showOnlySaleableProducts: true,
			showOnlyThisPosProducts: true
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	render(): React.ReactNode {


		// Este onInputChange sólo lanza con el teclado físico
		const onInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
			const currentValue = ev.target.value;
			this.setState({ input: currentValue }, () => doSearch());
			this.keyboardRef!.setInput(currentValue);
		};

		const onClearInput = () => this.setState({ input: '' }, () => {
			this.keyboardRef!.clearInput();
			doSearch();
		});

		const doSearch = () => {
			this.props.onSearchProducts(this.state.input, this.state.showOnlyThisPosProducts, this.state.showOnlySaleableProducts);
		};

		const onKeyboardChange = (input: string) => {
			this.setState({ input }, doSearch);
		};

		const onKeyboardKeyPress = (button: string) => {
			if (button === '{shift}' || button === '{lock}')
				toggleLayout();
		};

		const toggleLayout = () => this.setState({ layoutName: this.state.layoutName === 'default' ? 'shift' : 'default' });

		const onShowOnlySaleableProducts = () => this.setState({ showOnlySaleableProducts: !this.state.showOnlySaleableProducts }, doSearch);

		const onShowOnlyThisPosProducts = () => this.setState({ showOnlyThisPosProducts: !this.state.showOnlyThisPosProducts }, doSearch);

		const showDetails = async (product: ProductForSaleDTO) => await Command.execute('showProductDetails', product, SalesStore.getLocation());


		const products = this.props.products.map((product, index) => {

			const selectProduct = () => this.props.onProductSelected(product);

			return <li key={index} onClick={selectProduct}>
				<Cols className='text-14'>
					<Col width='*'>
						{product.SaleFormatName}
						<div className='hidden-lg'>
							{product.PriceLookUpCode && <small className='m-r-sm'>{product.PriceLookUpCode}</small>}
							{product.Barcode && <small className='m-r-sm'>{product.Barcode}</small>}
							<small>{product.FamilyName}</small>
						</div>
					</Col>

					<Col width='150px' className='visible-lg'>
						{product.FamilyName}
					</Col>
					<Col width='100px' className='visible-lg'>
						{product.PriceLookUpCode}
					</Col>
					<Col width='100px' className='visible-lg'>
						{product.Barcode}
					</Col>
					<Col width='100px' className='text-right'>
						{Format.asMoney(product.Price)}
					</Col>
					<Col width='50px' className='text-center text-info' onClick={async (e: React.MouseEvent) => {
						e.stopPropagation();
						e.preventDefault();

						await showDetails(product);
					}}>
						<i className='fa fa-info-circle'></i>
					</Col>
				</Cols>
			</li>;
		});

		const hasSearchInOtherPosPermission = SessionProvider.hasPermission(Permission.Pos_SearchProductInOtherPos);

		return <Flex.Vertical>
			<Flex.Item>
				<Cols className='list-header'>
					<Col width='*' onClick={() => this.props.onSortProductsBy(SortMode.SaleFormatName)}>
						{Res.Web.SEARCH_PRODUCT_BY_TEXT_PRODUCT_CLM()}
						{this.props.sortBy === SortMode.SaleFormatName && <i className='fa fa-sort-amount-asc m-l-xs'></i>}
					</Col>
					<Col width='150px' className='visible-lg' onClick={() => this.props.onSortProductsBy(SortMode.Family)}>
						{Res.Web.SEARCH_PRODUCT_BY_TEXT_FAMILY_CLM()}
						{this.props.sortBy === SortMode.Family && <i className='fa fa-sort-amount-asc m-l-xs'></i>}
					</Col>
					<Col width='100px' className='visible-lg' onClick={() => this.props.onSortProductsBy(SortMode.PriceLookUpCode)}>
						{Res.Web.SEARCH_PRODUCT_BY_TEXT_PLU_CLM()}
						{this.props.sortBy === SortMode.PriceLookUpCode && <i className='fa fa-sort-amount-asc m-l-xs'></i>}
					</Col>
					<Col width='100px' className='visible-lg' onClick={() => this.props.onSortProductsBy(SortMode.Barcode)}>
						{Res.Web.SEARCH_PRODUCT_BY_TEXT_BARCODE_CLM()}
						{this.props.sortBy === SortMode.Barcode && <i className='fa fa-sort-amount-asc m-l-xs'></i>}
					</Col>
					<Col width='100px' className='text-right' onClick={() => this.props.onSortProductsBy(SortMode.Price)}>
						{Res.Web.SEARCH_PRODUCT_BY_TEXT_PRICE_CLM()}
						{this.props.sortBy === SortMode.Price && <i className='fa fa-sort-amount-asc m-l-xs'></i>}
					</Col>
					{/* Es un poco más que el tamaño de la columna en cada item para disimular el efecto cuando aparece o desaparece la barra de scroll */}
					<Col width='75px'>
					</Col>
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				<ul className='list'>
					{products}
				</ul>
			</Flex.Item>

			<Flex.Item className='keyboard-container'>

				<div className='input-group p-l p-r p-t'>
					<input type='text' className='form-control' value={this.state.input} onChange={onInputChange} placeholder={Res.Web.SEARCH_PRODUCT_BY_TEXT_SEARCH_PLACEHOLDER()} />
					<span className='input-group-btn'><TextBoxButton buttonStyle='info' outline={true} className='fa fa-times' onClick={onClearInput} /></span>
				</div>

				<Row className='text-center'>
					<Column width='one-half'>
						<div className='checkbox abc-checkbox'>
							<input id='soldOnlyThisPosChk' type='checkbox' checked={hasSearchInOtherPosPermission ? this.state.showOnlyThisPosProducts : true} onChange={onShowOnlyThisPosProducts} disabled={!hasSearchInOtherPosPermission} />
							<label htmlFor='soldOnlyThisPosChk'>{Res.Web.SEARCH_PRODUCT_BY_TEXT_SOLD_ONLY_THIS_POS_CHK()}</label>
						</div>
					</Column>
					<Column width='one-half'>
						<div className='checkbox abc-checkbox'>
							<input id='soldOnlysaleableChk' type='checkbox' checked={this.state.showOnlySaleableProducts} onChange={onShowOnlySaleableProducts} />
							<label htmlFor='soldOnlysaleableChk'>{Res.Web.SEARCH_PRODUCT_BY_TEXT_SOLD_ONLY_SALEABLE_CHK()}</label>
						</div>
					</Column>
				</Row>
				<Keyboard
					keyboardRef={(r: any) => this.keyboardRef = r}
					layoutName={this.state.layoutName}
					onChange={onKeyboardChange}
					preventMouseDownDefault={true}
					onKeyPress={onKeyboardKeyPress}
					layout={keyboardConfig.layout}
					display={keyboardConfig.display}
					mergeDisplay={!!keyboardConfig.display}
					buttonTheme={[
						{
							class: 'hg-button-special',
							buttons: '{tab} {shift} {lock} {bksp} {enter}'
						}
					]}
				/>
			</Flex.Item>
		</Flex.Vertical >;
	}
}
